// Optimized VilleVillageSettings.jsx with server-side pagination
import React, { useState, useEffect } from 'react';
import { AlertCircle, MapPin, Upload, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { toast } from 'react-toastify';
import Papa from 'papaparse';

// Import components
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';

// ✅ OPTIMIZED: Paginated GraphQL Queries
const GET_VILLES_PAGINATED = gql`
  query GetVillesPaginated($paginationInput: PaginationInput) {
    villesPaginated(paginationInput: $paginationInput) {
      villes {
        id
        nomVilleVillage
        regionId
        createdAt
        updatedAt
        region {
          id
          nomRegion
        }
        users {
          id
        }
        pharmacies {
          id
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
      }
    }
  }
`;

// Keep lightweight queries for dropdowns
const GET_REGIONS = gql`
  query GetRegions {
    regions {
      id
      nomRegion
    }
  }
`;

// For import functionality - get all villes to check duplicates
const GET_ALL_VILLES = gql`
  query GetAllVilles {
    villes {
      id
      nomVilleVillage
      regionId
      region {
        id
        nomRegion
      }
    }
  }
`;

// Mutations remain the same
const CREATE_VILLE = gql`
  mutation CreateVille($createVilleInput: CreateVilleInput!) {
    createVille(createVilleInput: $createVilleInput) {
      id
      nomVilleVillage
      regionId
      region {
        id
        nomRegion
      }
      createdAt
      updatedAt
    }
  }
`;

const CREATE_REGION = gql`
  mutation CreateRegion($createRegionInput: CreateRegionInput!) {
    createRegion(createRegionInput: $createRegionInput) {
      id
      nomRegion
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_VILLE = gql`
  mutation UpdateVille($updateVilleInput: UpdateVilleInput!) {
    updateVille(updateVilleInput: $updateVilleInput) {
      id
      nomVilleVillage
      regionId
      region {
        id
        nomRegion
      }
      createdAt
      updatedAt
    }
  }
`;

const DELETE_VILLE = gql`
  mutation DeleteVille($id: ID!) {
    removeVille(id: $id)
  }
`;

const VilleVillageSettings = () => {
  // ✅ PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('nomVilleVillage');
  const [sortOrder, setSortOrder] = useState('asc');

  // Form and modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVille, setCurrentVille] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    nomVilleVillage: '',
    regionId: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // CSV Import States
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importStep, setImportStep] = useState(1);

  // ✅ OPTIMIZED: Paginated villes query
  const { 
    data: villesData, 
    loading: villesLoading, 
    error: villesError, 
    refetch: refetchVilles 
  } = useQuery(GET_VILLES_PAGINATED, {
    variables: {
      paginationInput: {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sortBy,
        sortOrder
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });

  // Lightweight queries for dropdowns
  const { 
    data: regionsData, 
    loading: regionsLoading 
  } = useQuery(GET_REGIONS);

  // For import functionality
  const { 
    data: allVillesData,
    refetch: refetchAllVilles
  } = useQuery(GET_ALL_VILLES, {
    fetchPolicy: 'cache-and-network'
  });

  // ✅ DEBOUNCED SEARCH
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Mutations with optimized refetch
  const [createVille, { loading: createLoading }] = useMutation(CREATE_VILLE, {
    onCompleted: (data) => {
      setIsModalOpen(false);
      setFormData({ nomVilleVillage: '', regionId: '' });
      setFormErrors({});
      toast.success('Ville/Village créé avec succès');
      // ✅ OPTIMIZED: Only refetch current page
      refetchVilles();
    },
    onError: (error) => {
      console.error('Create ville error:', error);
      if (error.graphQLErrors?.[0]?.message?.includes('unique') || 
          error.graphQLErrors?.[0]?.message?.includes('existe déjà')) {
        setFormErrors({ nomVilleVillage: 'Une ville/village avec ce nom existe déjà dans cette région' });
      } else {
        toast.error('Erreur lors de la création de la ville/village');
      }
    }
  });

  const [createRegion] = useMutation(CREATE_REGION, {
    refetchQueries: [{ query: GET_REGIONS }],
    onError: (error) => {
      console.error('Create region error:', error);
    }
  });

  const [updateVille, { loading: updateLoading }] = useMutation(UPDATE_VILLE, {
    onCompleted: (data) => {
      setIsModalOpen(false);
      setCurrentVille(null);
      setFormData({ nomVilleVillage: '', regionId: '' });
      setFormErrors({});
      toast.success('Ville/Village mis à jour avec succès');
      // ✅ OPTIMIZED: Only refetch current page
      refetchVilles();
    },
    onError: (error) => {
      console.error('Update ville error:', error);
      if (error.graphQLErrors?.[0]?.message?.includes('unique') || 
          error.graphQLErrors?.[0]?.message?.includes('existe déjà')) {
        setFormErrors({ nomVilleVillage: 'Une ville/village avec ce nom existe déjà dans cette région' });
      } else {
        toast.error('Erreur lors de la mise à jour de la ville/village');
      }
    }
  });

  const [deleteVille, { loading: deleteLoading }] = useMutation(DELETE_VILLE, {
    onCompleted: () => {
      setIsConfirmDeleteOpen(false);
      setCurrentVille(null);
      toast.success('Ville/Village supprimé avec succès');
      // ✅ OPTIMIZED: Check if we need to go back a page
      const totalItems = villesData?.villesPaginated?.pagination?.totalItems || 0;
      const totalPages = Math.ceil((totalItems - 1) / itemsPerPage);
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(totalPages);
      } else {
        refetchVilles();
      }
    },
    onError: (error) => {
      console.error('Delete ville error:', error);
      toast.error('Erreur lors de la suppression de la ville/village');
    }
  });

  // ✅ OPTIMIZED: Extract data from paginated response
  const villes = villesData?.villesPaginated?.villes || [];
  const pagination = villesData?.villesPaginated?.pagination;
  const regions = regionsData?.regions || [];
  const allVilles = allVillesData?.villes || [];

  // ✅ PAGINATION HANDLERS
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleSearch = (search) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  // Form handlers
  const handleCreateNew = () => {
    setCurrentVille(null);
    setFormData({
      nomVilleVillage: '',
      regionId: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (ville) => {
    setCurrentVille(ville);
    setFormData({
      nomVilleVillage: ville.nomVilleVillage,
      regionId: ville.regionId
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (ville) => {
    setCurrentVille(ville);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!currentVille) return;
    
    try {
      await deleteVille({
        variables: { id: currentVille.id }
      });
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nomVilleVillage?.trim()) {
      errors.nomVilleVillage = 'Le nom de la ville/village est requis';
    }
    
    if (!formData.regionId) {
      errors.regionId = 'La région est requise';
    }
    
    // Check for duplicate ville name in the same region (client-side validation)
    if (formData.nomVilleVillage?.trim() && formData.regionId) {
      const normalizedName = formData.nomVilleVillage.trim().toLowerCase();
      const duplicate = allVilles.find(ville => 
        ville.nomVilleVillage.toLowerCase() === normalizedName && 
        ville.regionId === formData.regionId &&
        (!currentVille || ville.id !== currentVille.id)
      );
      
      if (duplicate) {
        errors.nomVilleVillage = 'Une ville/village avec ce nom existe déjà dans cette région';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      const villeData = {
        nomVilleVillage: formData.nomVilleVillage.trim(),
        regionId: formData.regionId
      };

      if (currentVille) {
        await updateVille({
          variables: {
            updateVilleInput: {
              id: currentVille.id,
              ...villeData
            }
          }
        });
      } else {
        await createVille({
          variables: {
            createVilleInput: villeData
          }
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
    }
  };

  // CSV Import Functions
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Seuls les fichiers CSV sont acceptés');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 5MB');
      return;
    }

    setCsvFile(file);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        if (results.errors && results.errors.length > 0) {
          console.error('CSV parse errors:', results.errors);
          toast.error('Erreur lors de la lecture du fichier CSV');
          return;
        }

        const requiredColumns = ['City', 'Region'];
        const headers = results.meta.fields || [];
        const missingColumns = requiredColumns.filter(col => !headers.includes(col));
        
        if (missingColumns.length > 0) {
          toast.error(`Colonnes manquantes: ${missingColumns.join(', ')}`);
          return;
        }

        const processedData = results.data
          .filter(row => row.City && row.Region)
          .map(row => ({
            city: row.City.trim(),
            region: row.Region.trim()
          }));

        if (processedData.length === 0) {
          toast.error('Aucune donnée valide trouvée dans le fichier CSV');
          return;
        }

        setCsvData(processedData);
        setCsvPreview(processedData.slice(0, 5));
        setImportStep(2);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        toast.error('Erreur lors de la lecture du fichier CSV');
      }
    });
  };

  const processImport = async () => {
    if (!csvData || csvData.length === 0) {
      toast.error('Aucune donnée à importer');
      return;
    }

    setIsImporting(true);
    setImportStep(3);

    const results = {
      processed: 0,
      regionsCreated: 0,
      villesCreated: 0,
      duplicates: 0,
      errors: []
    };

    try {
      // Get existing data
      const existingRegions = regions.reduce((acc, region) => {
        acc[region.nomRegion.toLowerCase()] = region;
        return acc;
      }, {});

      const existingVilles = allVilles.reduce((acc, ville) => {
        const key = `${ville.nomVilleVillage.toLowerCase()}_${ville.regionId}`;
        acc[key] = ville;
        return acc;
      }, {});

      // Process regions first
      const regionMap = { ...existingRegions };
      const uniqueRegions = [...new Set(csvData.map(row => row.region))];
      
      for (const regionName of uniqueRegions) {
        const regionKey = regionName.toLowerCase();
        if (!regionMap[regionKey]) {
          try {
            const response = await createRegion({
              variables: {
                createRegionInput: {
                  nomRegion: regionName
                }
              }
            });
            if (response.data?.createRegion) {
              regionMap[regionKey] = response.data.createRegion;
              results.regionsCreated++;
            }
          } catch (error) {
            console.error(`Error creating region ${regionName}:`, error);
            if (error.message?.includes('Une région avec ce nom existe déjà')) {
              const existingRegion = regions.find(r => r.nomRegion.toLowerCase() === regionKey);
              if (existingRegion) {
                regionMap[regionKey] = existingRegion;
                console.log(`Using existing region: ${regionName}`);
              } else {
                results.errors.push(`Erreur région "${regionName}": ${error.message}`);
              }
            } else {
              results.errors.push(`Erreur création région "${regionName}": ${error.message}`);
            }
          }
        }
      }

      // Process villes
      for (const row of csvData) {
        results.processed++;
        
        const regionKey = row.region.toLowerCase();
        const region = regionMap[regionKey];
        
        if (!region) {
          results.errors.push(`Région non trouvée pour "${row.city}": ${row.region}`);
          continue;
        }

        const villeKey = `${row.city.toLowerCase()}_${region.id}`;
        
        if (existingVilles[villeKey]) {
          results.duplicates++;
          continue;
        }

        try {
          const response = await createVille({
            variables: {
              createVilleInput: {
                nomVilleVillage: row.city,
                regionId: region.id
              }
            }
          });
          
          if (response.data?.createVille) {
            results.villesCreated++;
            existingVilles[villeKey] = response.data.createVille;
          }
        } catch (error) {
          console.error(`Error creating ville ${row.city}:`, error);
          results.errors.push(`Erreur création ville "${row.city}": ${error.message}`);
        }
      }

      setImportResults(results);

      if (results.errors.length === 0) {
        toast.success(`Import terminé avec succès! ${results.regionsCreated} régions et ${results.villesCreated} villes créées.`);
      } else {
        toast.warning(`Import terminé avec ${results.errors.length} erreur(s).`);
      }

      // ✅ OPTIMIZED: Reset pagination and refetch
      setCurrentPage(1);
      await Promise.all([
        refetchVilles(),
        refetchAllVilles()
      ]);

    } catch (error) {
      console.error('Import error:', error);
      toast.error('Erreur lors de l\'import: ' + error.message);
      results.errors.push('Erreur générale: ' + error.message);
      setImportResults(results);
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvPreview([]);
    setImportResults(null);
    setImportStep(1);
    setIsImportModalOpen(false);
  };

  // Table columns configuration
  const columns = [
    {
      Header: 'Nom',
      accessor: 'nomVilleVillage',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <MapPin size={16} className="mr-2 text-teal-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      Header: 'Région',
      accessor: 'region',
      sortable: true,
      searchable: true,
      Cell: ({ row }) => (
        <span className="text-gray-600">
          {row.region?.nomRegion || 'N/A'}
        </span>
      )
    },
    {
      Header: 'Utilisateurs',
      accessor: 'usersCount',
      sortable: true,
      searchable: false,
      Cell: ({ row }) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {row.users?.length || 0}
        </span>
      )
    },
    {
      Header: 'Pharmacies',
      accessor: 'pharmaciesCount',
      sortable: true,
      searchable: false,
      Cell: ({ row }) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {row.pharmacies?.length || 0}
        </span>
      )
    },
    {
      Header: 'Date de création',
      accessor: 'createdAt',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="text-gray-500 text-sm">
          {new Date(value).toLocaleDateString('fr-FR')}
        </span>
      )
    }
  ];

  // ✅ OPTIMIZED: Pass pagination data to DataTableSettings
  const tableProps = {
    title: "Gestion des Villes/Villages",
    subtitle: `${pagination?.totalItems || 0} ville(s)/village(s) au total`,
    data: villes,
    columns,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onCreate: handleCreateNew,
    loading: villesLoading,
    emptyMessage: "Aucune ville/village trouvée",
    // ✅ NEW: Pass pagination props
    pagination: {
      currentPage: pagination?.currentPage || 1,
      totalPages: pagination?.totalPages || 1,
      totalItems: pagination?.totalItems || 0,
      itemsPerPage: pagination?.itemsPerPage || itemsPerPage,
      hasNextPage: pagination?.hasNextPage || false,
      hasPreviousPage: pagination?.hasPreviousPage || false,
      onPageChange: handlePageChange,
      onItemsPerPageChange: handleItemsPerPageChange
    },
    // ✅ NEW: Pass search and sort props
    search: {
      value: searchTerm,
      onChange: handleSearch,
      placeholder: "Rechercher par nom de ville ou région..."
    },
    sort: {
      column: sortBy,
      direction: sortOrder,
      onSort: handleSort
    }
  };

  const isLoading = villesLoading || regionsLoading;
  const isSubmitting = createLoading || updateLoading;

  return (
    <div className="space-y-6">
      {/* ✅ OPTIMIZED: Enhanced DataTableSettings with server-side pagination */}
      <DataTableSettings {...tableProps} />

      {/* Import Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setIsImportModalOpen(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          <Upload size={16} className="mr-2" />
          Importer CSV
        </button>
      </div>

      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentVille ? 'Modifier la Ville/Village' : 'Créer une Ville/Village'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la ville/village *
            </label>
            <input
              type="text"
              name="nomVilleVillage"
              value={formData.nomVilleVillage}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                formErrors.nomVilleVillage ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Nom de la ville ou village"
            />
            {formErrors.nomVilleVillage && (
              <p className="mt-1 text-sm text-red-600">{formErrors.nomVilleVillage}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Région *
            </label>
            <select
              name="regionId"
              value={formData.regionId}
              onChange={handleInputChange}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                formErrors.regionId ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une région</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.nomRegion}</option>
              ))}
            </select>
            {formErrors.regionId && (
              <p className="mt-1 text-sm text-red-600">{formErrors.regionId}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting && <Loader2 size={16} className="mr-2 animate-spin" />}
              {currentVille ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* Delete Confirmation Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        title="Confirmer la suppression"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertCircle className="h-10 w-10 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Supprimer la ville/village
              </h3>
              <p className="text-gray-600">
                Êtes-vous sûr de vouloir supprimer "{currentVille?.nomVilleVillage}" ? 
                Cette action est irréversible.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setIsConfirmDeleteOpen(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              onClick={confirmDelete}
              disabled={deleteLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {deleteLoading && <Loader2 size={16} className="mr-2 animate-spin" />}
              Supprimer
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* CSV Import Modal */}
      <ModalComponent
        isOpen={isImportModalOpen}
        onClose={resetImport}
        title="Importer des Villes/Villages"
        size="large"
      >
        <div className="space-y-6">
          {/* Step 1: File Selection */}
          {importStep === 1 && (
            <div className="space-y-4">
              <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Format CSV requis
                </h4>
                <p className="text-sm text-blue-700 mb-2">
                  Votre fichier CSV doit contenir les colonnes suivantes :
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>City</strong> : Nom de la ville/village</li>
                  <li>• <strong>Region</strong> : Nom de la région</li>
                </ul>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-file-input"
                />
                <label htmlFor="csv-file-input" className="cursor-pointer">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-900">
                      Cliquez pour sélectionner un fichier CSV
                    </p>
                    <p className="text-sm text-gray-500">ou glissez-déposez ici</p>
                  </div>
                </label>
              </div>

              {csvFile && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700">
                    Fichier sélectionné: <strong>{csvFile.name}</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {importStep === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">
                  Aperçu des données ({csvData.length} ligne(s))
                </h4>
                <button
                  onClick={() => setImportStep(1)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Modifier le fichier
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Ville/Village
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Région
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvPreview.map((row, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.city}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {row.region}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {csvData.length > 5 && (
                <p className="text-sm text-gray-500 text-center">
                  ... et {csvData.length - 5} ligne(s) supplémentaire(s)
                </p>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setImportStep(1)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Retour
                </button>
                <button
                  onClick={processImport}
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  Commencer l'import
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {importStep === 3 && (
            <div className="space-y-4">
              {isImporting ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600 mr-3" />
                  <span className="text-gray-600">Import en cours...</span>
                </div>
              ) : importResults && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{importResults.processed}</div>
                      <div className="text-sm text-blue-700">Traités</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">{importResults.regionsCreated}</div>
                      <div className="text-sm text-green-700">Régions créées</div>
                    </div>
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">{importResults.villesCreated}</div>
                      <div className="text-sm text-emerald-700">Villes créées</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">{importResults.duplicates}</div>
                      <div className="text-sm text-yellow-700">Doublons ignorés</div>
                    </div>
                  </div>

                  {importResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-medium text-red-800 mb-2 flex items-center">
                        <XCircle className="w-5 h-5 mr-2" />
                        Erreurs ({importResults.errors.length})
                      </h4>
                      <div className="max-h-32 overflow-y-auto">
                        {importResults.errors.map((error, index) => (
                          <p key={index} className="text-sm text-red-700">• {error}</p>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={resetImport}
                      className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                    >
                      Terminé
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </ModalComponent>
    </div>
  );
};

export default VilleVillageSettings;